import {cubicBezier, type Variants} from 'framer-motion';

export const InViewPortTrigger: Variants = {
    /**
     * Defines the animation behavior when the element is in the viewport.
     * It staggers the child animations, with each child animating after a delay based on the `stagger` value.
     *
     * @param stagger A number representing the time (in seconds) between the start of each child animation. This controls the delay between child animations.
     * @returns An object containing the `transition` properties for animating child elements when the parent element is in view:
     * - `staggerChildren`: Staggers the animations of child elements based on the provided `stagger` value.
     * - `staggerDirection`: Controls the direction of the stagger, with `1` meaning the children animate in order (forward).
     * - `when`: Specifies when the parent animation occurs relative to the children, with `beforeChildren` ensuring the parent animates before the children.
     */
    inView(stagger) {
        return {
            transition: {
                staggerChildren: stagger,
                staggerDirection: 1,
                when: 'beforeChildren'
            }
        };
    },
    /**
     * Defines the animation behavior when the element is out of the viewport.
     * It staggers the child animations in reverse order, with each child animating after a delay based on the `stagger` value.
     *
     * @param stagger A number representing the time (in seconds) between the start of each child animation. This controls the delay between child animations.
     * @returns An object containing the `transition` properties for animating child elements when the parent element is out of view:
     * - `staggerChildren`: Staggers the animations of child elements based on the provided `stagger` value.
     * - `staggerDirection`: Controls the direction of the stagger, with `-1` meaning the children animate in reverse order (backward).
     * - `when`: Specifies when the parent animation occurs relative to the children, with `beforeChildren` ensuring the parent animates before the children.
     */
    outOfView(stagger) {
        return {
            transition: {
                staggerChildren: stagger,
                staggerDirection: -1,
                when: 'beforeChildren'
            }
        };
    }
};

export const FadeInAnimation: Variants = {
    inView: {
        opacity: 1,
        transition: {
            duration: 0.5,
            // eslint-disable-next-line @nfq/no-magic-numbers
            ease: cubicBezier(0.40, 0.80, 0.74, 1),
            staggerChildren: 0.2,
            staggerDirection: 1,
            when: 'beforeChildren'
        },
        y: '0px'
    },
    outOfView: {
        opacity: 0,
        y: '50px'
    }
};